/* eslint-disable @next/next/no-img-element */
/* eslint-disable react/jsx-curly-newline */
import React, { useEffect, useState, useContext } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import StaticDayPassesByLocation from '@components/DayPassesByLocation/StaticDayPassesByLocation';
import ExploreLocations from '@components/ExploreLocations/ExploreLocations';
import GiftCard from '@components/GiftCard/GiftCard';
import HotelPartner from '@components/HotelPartner/HotelPartner';
import CounterWithSearch from '@components/CounterWithSearch/CounterWithSearch';
import { useAmplitudeExperimentAB } from '@hooks/useAmplitudeExperiment';
import { PopularHotel } from '@customTypes/popular-hotel-data';
import { useEvents } from '@events/EventsProvider';
import useOnScreen from '@hooks/useOnScreen';
import useDoOnce from '@hooks/useDoOnce';
import { VIEW_HOMEPAGE, UseCaseMerchandisingEvents } from '@constants/amplitudeEvents';
import HotelApi from '@customTypes/hotel-api';
import { store } from '@context/store';
import { HomePageHotelsContext } from '@context/HomePageHotelsContext';
import HomePageMarketingModule from '@components/MarketingModule/HomePageMarketingModule';
import RoutingPath from '@constants/routingPath';
import { GetDefaultSearchState } from '@context/SearchContext2025';
import { GenerateSearchUrl } from '@helpers/url';
import { PoolIcon, TickIcon, HeartIcon } from '@resortpass/solar-icons';
import Marquee from 'react-fast-marquee';
import Image from 'next/image';
import cnnLogo from '@assets/images/brand-logos/1-cnn-logo.svg';
import nytLogo from '@assets/images/brand-logos/2-nyt-logo.svg';
import todayLogo from '@assets/images/brand-logos/3-today-logo.svg';
import forbesLogo from '@assets/images/brand-logos/4-forbes-logo.svg';
import travelAndLeisureLogo from '@assets/images/brand-logos/5-travel-leisure-logo.svg';
import peopleLogo from '@assets/images/brand-logos/6-people-logo.svg';
import buzzfeedLogo from '@assets/images/brand-logos/7-buzzfeed-logo.svg';
import cntLogo from '@assets/images/brand-logos/8-cnt-logo.svg';
import wsjLogo from '@assets/images/brand-logos/9-wsj-logo.svg';
import latimesLogo from '@assets/images/brand-logos/10-lat-logo.svg';
import popsugarLogo from '@assets/images/brand-logos/11-popsugar-logo.svg';
import parentsLogo from '@assets/images/brand-logos/12-parents-logo.svg';
import pgLogo from '@assets/images/brand-logos/13-pg-logo.svg';
import morningbrewLogo from '@assets/images/brand-logos/14-morning-brew-logo.svg';
import HomePagePopularHotels from './HomePagePopularHotels';
import HomePageNearHotels from './HomePageNearHotels';
import HotelCardsLoadingSkeleton from './HotelCardsLoadingSkeleton';
import HomePageHotelCardCollection, { MapPopularHotels, MapHotelApi } from './HotelCardCollection';
import HomePageHandPickedCollection from './HandPickedCollection';

type IndexPageProps = {
  popularHotels: PopularHotel[];
  newAndNotableHotels: HotelApi[];
  userCityAndState: string;
};

export default function HomePage({
  popularHotels,
  newAndNotableHotels,
  userCityAndState,
}: IndexPageProps) {
  const router = useRouter();
  const [selectedCityShort, setSelectedCityShort] = useState<string>();
  const [amplitudePageViewEventSent, setAmplitudePageViewEventSent] = useState<boolean>(false);

  const { amplitude, track } = useEvents();

  const { homePageHotels, isFetchComplete, isLoading, selectedCity, selectedCityId } =
    useContext(HomePageHotelsContext);

  const {
    state: { userLocation },
  } = useContext(store);

  const isUseCaseExperiment = useAmplitudeExperimentAB('use-case-merchandising') === 'treatment';

  useEffect(() => {
    if (homePageHotels && selectedCity && homePageHotels.length > 0) {
      const stateCodeValue = homePageHotels[0].code;
      if (stateCodeValue) {
        const city = `${selectedCity.split(',')[0]}, ${stateCodeValue}`;
        setSelectedCityShort(city);
      }
    }
  }, [homePageHotels, selectedCity]);

  useEffect(() => {
    if (!amplitudePageViewEventSent && amplitude && isFetchComplete) {
      const hotels: HotelApi[] | PopularHotel[] =
        homePageHotels.length > 0 ? homePageHotels : popularHotels;

      const hotSpotHotels = (hotels as (HotelApi | PopularHotel)[]).filter(
        (hotel) => !!hotel.hotSpot,
      );

      track(VIEW_HOMEPAGE, {
        hot_spot_hotels: hotSpotHotels.map((hotel) => hotel.name),
      });

      setAmplitudePageViewEventSent(true);
    }
  }, [
    amplitude,
    amplitudePageViewEventSent,
    homePageHotels,
    isFetchComplete,
    popularHotels,
    track,
  ]);

  // -------------------------------------------------------------------
  // Use-Case Collection Tracking --------------------------------------
  // -------------------------------------------------------------------
  const trackView = (eventName: string, options: any = {}) =>
    useDoOnce(() => {
      if (amplitude) {
        track(eventName, options);
      }
    });

  // UseCase Visibility Tracking Targets
  const [isNearCityCollectionVisible, nearCityCollectionRef] = useOnScreen();
  const [isPopularHotelsCollectionVisible, popularHotelsCollectionRef] = useOnScreen();
  const [isNewAndNotableCollectionVisible, newAndNotableCollectionRef] = useOnScreen();
  const [isHandPickedCollectionVisible, handPickedCollectionRef] = useOnScreen();

  // UseCase Visibility Tracking Events
  const viewNearCityCollectionEvent = trackView(
    UseCaseMerchandisingEvents.VIEW_HOME_PAGE_NEAR_CITY_COLLECTION,
  );
  const viewPopularHotelsCollectionEvent = trackView(
    UseCaseMerchandisingEvents.VIEW_HOME_PAGE_POPULAR_HOTELS_COLLECTION,
  );
  const viewNewAndNotableCollectionEvent = trackView(
    UseCaseMerchandisingEvents.VIEW_HOME_PAGE_NEW_AND_NOTABLE_COLLECTION,
  );
  const viewHandPickedCollectionEvent = trackView(
    UseCaseMerchandisingEvents.VIEW_HAND_PICKED_COLLECTION,
  );

  // Track any targets that are viewed
  useEffect(() => {
    if (isNearCityCollectionVisible) viewNearCityCollectionEvent();
    if (isPopularHotelsCollectionVisible) viewPopularHotelsCollectionEvent();
    if (isNewAndNotableCollectionVisible) viewNewAndNotableCollectionEvent();
    if (isHandPickedCollectionVisible) viewHandPickedCollectionEvent();
  }, [
    isNearCityCollectionVisible,
    isPopularHotelsCollectionVisible,
    isNewAndNotableCollectionVisible,
    isHandPickedCollectionVisible,
    viewNearCityCollectionEvent,
    viewPopularHotelsCollectionEvent,
    viewNewAndNotableCollectionEvent,
    viewHandPickedCollectionEvent,
  ]);

  // -------------------------------------------------------------------
  // Use-Case Collection UI Handlers -----------------------------------
  // -------------------------------------------------------------------
  const handleSeeAllNearCity = async () => {
    if (userLocation) {
      if (amplitude) {
        try {
          await track(UseCaseMerchandisingEvents.HOME_PAGE_NEAR_CITY_COLLECTION_SEE_ALL_CLICK, {});
        } catch (e) {
          // continue if tracking fails
        }
      }

      const searchState = GetDefaultSearchState();
      searchState.city = userLocation.city_name;
      searchState.state_code = userLocation.state_code;
      searchState.country_code = userLocation.country_code;
      searchState.search_term = userLocation.name;
      searchState.date = 'any';
      router.push(GenerateSearchUrl(searchState));
    }
  };

  const handleSeeAllNewAndNotable = async () => {
    if (amplitude) {
      try {
        await track(
          UseCaseMerchandisingEvents.HOME_PAGE_NEW_AND_NOTABLE_COLLECTION_SEE_ALL_CLICK,
          {},
        );
      } catch (e) {
        // continue if tracking fails
      }
    }
    window.sessionStorage.removeItem('smart_calendar_date');
    window.sessionStorage.removeItem('srp_filters');
    window.sessionStorage.removeItem('show_only_available_filter');
    router.push(`${RoutingPath.SRP}?shownew=true&nodate=true`);
  };

  const trackHotelClick = async (eventName: string, data: any = {}) => {
    try {
      await track(eventName, data);
    } catch (e) {
      // continue if tracking fails
    }
  };

  const trackHandPickedCollectionClick = async ({ title }: { title: string }) => {
    if (amplitude) {
      try {
        await track(UseCaseMerchandisingEvents.HAND_PICKED_COLLECTION_CLICK, { name: title });
      } catch (e) {
        // continue of tracking fails
      }
    }
  };

  const getSRPUrl = () => {
    if (userLocation) {
      const searchState = GetDefaultSearchState();
      searchState.city = userLocation.city_name;
      searchState.state_code = userLocation.state_code;
      searchState.country_code = userLocation.country_code;
      searchState.search_term = userLocation.name;
      return `${window.location.origin}${GenerateSearchUrl(searchState)}`;
    }
    return '';
  };

  const defaultPageTitle = 'ResortPass | Book a Day Pass to a Hotel starting at only $25';

  return (
    <>
      <Head>
        <title>{defaultPageTitle}</title>
        <meta property="og:title" content={defaultPageTitle} />
        <meta
          name="description"
          content="Book a hotel pool or spa day pass starting at just $25. Enjoy over 2,000 world-class resorts without the overnight stay."
        />
      </Head>

      <main className="flex flex-col mb-spacing-2xl">
        <div className="flex flex-col items-center bg-value-props-gradient gap-0 d:gap-6">
          <div className="flex flex-col d:flex-row d:max-w-1200px px-4 d:px-14 pt-6 d:pt-10 pb-8 gap-4 d:gap-24">
            <div className="flex items-start gap-2 d:gap-3">
              <div>
                <PoolIcon color="#0A215C" size={24} />
              </div>

              <div className="flex flex-col gap-1">
                <p className="font-rp-basetica text-sm text-solar-surface-brand-midnight">
                  Escape for the Day
                </p>
                <p className="font-rp-basetica text-sm text-solar-secondary">
                  Book pools, spas, cabanas, beaches & more at 2,000+ top hotels — no overnight
                  stay.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2 d:gap-3">
              <div>
                <TickIcon color="#0A215C" size={24} />
              </div>

              <div className="flex flex-col gap-1">
                <p className="font-rp-basetica text-sm text-solar-surface-brand-midnight">
                  Book with Confidence
                </p>
                <p className="font-rp-basetica text-sm text-solar-secondary">
                  Instant confirmation, easy check-in, and flexible cancellation options.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2 d:gap-3">
              <div>
                <HeartIcon color="#0A215C" size={24} />
              </div>

              <div className="flex flex-col gap-1">
                <p className="font-rp-basetica text-sm text-solar-surface-brand-midnight">
                  Backed by 100,000+ 5-star reviews
                </p>
                <p className="font-rp-basetica text-sm text-solar-secondary">
                  Join millions of guests and a growing community of daycation enthusiasts.
                </p>
              </div>
            </div>
          </div>
          <div className="w-full overflow-hidden pb-8">
            <Marquee speed={40} pauseOnHover autoFill>
              <div className="mx-8">
                <Image src={cnnLogo} alt="CNN" />
              </div>
              <div className="mx-8">
                <Image src={nytLogo} alt="NYT" />
              </div>
              <div className="mx-8">
                <Image src={todayLogo} alt="Today" />
              </div>
              <div className="mx-8">
                <Image src={forbesLogo} alt="Forbes" />
              </div>
              <div className="mx-8">
                <Image src={travelAndLeisureLogo} alt="Travel and Leisure" />
              </div>
              <div className="mx-8">
                <Image src={peopleLogo} alt="People" />
              </div>
              <div className="mx-8">
                <Image src={buzzfeedLogo} alt="Buzzfeed" />
              </div>
              <div className="mx-8">
                <Image src={cntLogo} alt="CNT" />
              </div>
              <div className="mx-8">
                <Image src={wsjLogo} alt="WSJ" />
              </div>
              <div className="mx-8">
                <Image src={latimesLogo} alt="LA Times" />
              </div>
              <div className="mx-8">
                <Image src={popsugarLogo} alt="Popsugar" />
              </div>
              <div className="mx-8">
                <Image src={parentsLogo} alt="Parents" />
              </div>
              <div className="mx-8">
                <Image src={pgLogo} alt="PG" />
              </div>
              <div className="mx-8">
                <Image src={morningbrewLogo} alt="Morning Brew" />
              </div>
            </Marquee>
          </div>
        </div>

        {isLoading ? <HotelCardsLoadingSkeleton /> : null}

        {isUseCaseExperiment ? (
          <>
            {!isLoading && homePageHotels && homePageHotels.length === 0 && popularHotels && (
              <HomePageHotelCardCollection
                innerRef={popularHotelsCollectionRef}
                title="Popular Hotels"
                hotels={MapPopularHotels(popularHotels.slice(0, 16))}
                onHotelClick={(h) =>
                  trackHotelClick(
                    UseCaseMerchandisingEvents.HOME_PAGE_POPULAR_HOTELS_COLLECTION_CLICK,
                    { name: h.name, url: h.url },
                  )
                }
              />
            )}

            {!isLoading && homePageHotels && homePageHotels.length > 0 ? (
              <HomePageHotelCardCollection
                innerRef={nearCityCollectionRef}
                title={`Near ${userCityAndState}`}
                hotels={MapHotelApi(homePageHotels)}
                showSeeAll
                onSeeAll={handleSeeAllNearCity}
                onHotelClick={(h) =>
                  trackHotelClick(UseCaseMerchandisingEvents.HOME_PAGE_NEAR_CITY_COLLECTION_CLICK, {
                    name: h.name,
                    url: h.url,
                  })
                }
              />
            ) : null}
          </>
        ) : (
          <>
            {!isLoading && homePageHotels && homePageHotels.length === 0 && popularHotels && (
              <HomePagePopularHotels popularHotels={popularHotels} />
            )}

            {!isLoading && homePageHotels && homePageHotels.length > 0 ? (
              <HomePageNearHotels
                hotels={homePageHotels}
                selectedCityId={selectedCityId}
                selectedCityShort={selectedCityShort}
                userCityAndState={userCityAndState}
              />
            ) : null}
          </>
        )}

        {isUseCaseExperiment && userLocation ? (
          <HomePageHandPickedCollection
            innerRef={handPickedCollectionRef}
            className="mt-32px"
            srpURL={getSRPUrl()}
            onCollectionClick={trackHandPickedCollectionClick}
          />
        ) : null}

        {isUseCaseExperiment && newAndNotableHotels && newAndNotableHotels.length > 0 ? (
          <HomePageHotelCardCollection
            title="New and notable"
            innerRef={newAndNotableCollectionRef}
            className="mt-32px"
            hotels={MapHotelApi(newAndNotableHotels)}
            showSeeAll
            onSeeAll={handleSeeAllNewAndNotable}
            onHotelClick={(h) =>
              trackHotelClick(
                UseCaseMerchandisingEvents.HOME_PAGE_NEW_AND_NOTABLE_COLLECTION_CLICK,
                { name: h.name, url: h.url },
              )
            }
          />
        ) : null}

        <div className="px-6 mt-20 mb-10">
          <HomePageMarketingModule />
        </div>

        <div className="pt-10">
          <ExploreLocations />
        </div>
        <GiftCard />
        <HotelPartner />
        <CounterWithSearch />
        <StaticDayPassesByLocation />
      </main>
    </>
  );
}
